import { useCallback } from 'react'
import { DatePicker, Form, Input, Select } from '@musinsa/pbo-core'
import type { FormInstance } from 'antd'
import type { SelectOptionType } from '@/components/content/SelectBoxTypes'
import { SHIPPER_TYPES } from '@/services/shipper/apis'
import { MultiValueInput } from '@/pages/inventory/inventoryStatus/components/MultiValueInput'
import { FilterCard, chipCodes, chipRange, chipSelect, chipText } from '../../dailyCloseShared/FilterCard'
import { clearShipperOnServiceType } from '../../dailyCloseShared/clearShipperOnServiceType'
import { FIELD_NAMES } from '../constants'
import type { DailyCloseProductFormValues } from '../filterToRequest'

interface Props {
  form: FormInstance
  initialValues: DailyCloseProductFormValues
  shipperOptions: SelectOptionType<string>[]
  canEditShipper: boolean
  /** 물류서비스타입을 고르면 화주를 비우는가 — 잠기지 않은 계정만(clearShipperOnServiceType). */
  clearsShipperOnServiceType: boolean
  onSearch: (values: DailyCloseProductFormValues) => void
  onReset: () => void
}

const SERVICE_TYPE_OPTIONS = SHIPPER_TYPES.map((type) => ({ value: type, label: type }))
const toChipOptions = (options: SelectOptionType<string>[]) => options.map((option) => ({ value: String(option.value), label: String(option.label) }))

export function DailyCloseProductFilter(props: Readonly<Props>) {
  const { form, initialValues, shipperOptions, canEditShipper, clearsShipperOnServiceType, onSearch, onReset } = props
  const shipperCode = Form.useWatch(FIELD_NAMES.shipperCode, form)
  const closeDateRange = Form.useWatch(FIELD_NAMES.closeDateRange, form)
  const brandName = Form.useWatch(FIELD_NAMES.brandName, form)
  const goodsCode = Form.useWatch(FIELD_NAMES.goodsCode, form)
  const barcodes = Form.useWatch(FIELD_NAMES.barcodes, form)
  const logisticsServiceType = Form.useWatch(FIELD_NAMES.logisticsServiceType, form)

  const handleReset = useCallback(() => {
    form.resetFields()
    onReset()
  }, [form, onReset])

  return (
    // ST10 선례와 동일하게 날짜 범위·화주/서비스 교차 조건을 부모의 순수 검증 함수에서 한 번에 검사한다.
    <Form<DailyCloseProductFormValues> form={form} initialValues={initialValues} onFinish={onSearch}>
      <FilterCard
        onReset={handleReset}
        fields={[
          {
            key: FIELD_NAMES.shipperCode,
            label: '화주',
            chip: chipSelect(toChipOptions(shipperOptions), shipperCode),
            control: (
              <Form.Item name={FIELD_NAMES.shipperCode} noStyle>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  options={shipperOptions}
                  disabled={!canEditShipper}
                  placeholder="화주 선택"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            ),
          },
          {
            key: FIELD_NAMES.closeDateRange,
            label: '마감일자',
            required: true,
            chip: chipRange(closeDateRange),
            control: (
              <Form.Item name={FIELD_NAMES.closeDateRange} noStyle>
                <DatePicker.RangePicker format="YYYY-MM-DD" style={{ width: '100%' }} />
              </Form.Item>
            ),
          },
          {
            key: FIELD_NAMES.brandName,
            label: '브랜드명',
            chip: chipText(brandName),
            control: (
              <Form.Item name={FIELD_NAMES.brandName} noStyle>
                <Input allowClear placeholder="브랜드명 입력" />
              </Form.Item>
            ),
          },
          {
            key: FIELD_NAMES.goodsCode,
            label: '상품코드',
            chip: chipText(goodsCode),
            control: (
              <Form.Item name={FIELD_NAMES.goodsCode} noStyle>
                <Input allowClear placeholder="상품코드 입력" />
              </Form.Item>
            ),
          },
          {
            key: FIELD_NAMES.barcodes,
            label: '바코드',
            chip: chipCodes(barcodes),
            control: (
              <Form.Item name={FIELD_NAMES.barcodes} noStyle>
                <MultiValueInput />
              </Form.Item>
            ),
          },
          {
            key: FIELD_NAMES.logisticsServiceType,
            label: '물류서비스타입',
            chip: chipSelect(SERVICE_TYPE_OPTIONS, logisticsServiceType),
            control: (
              <Form.Item name={FIELD_NAMES.logisticsServiceType} noStyle>
                <Select
                  allowClear
                  options={SERVICE_TYPE_OPTIONS}
                  placeholder="전체"
                  style={{ width: '100%' }}
                  onChange={(value?: string | null) => clearShipperOnServiceType(form, FIELD_NAMES.shipperCode, clearsShipperOnServiceType, value)}
                />
              </Form.Item>
            ),
          },
        ]}
      />
    </Form>
  )
}
